import { Button } from "@/components/ui";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { CheckCircle2, ChevronDown, TriangleAlert } from "lucide-react";
import {
  formatCurrency,
  getMatterEconomicDamagesSummary,
  type EconomicDamageLine,
} from "../matter-overview-utils";

function DamageLineStatus({
  statusTone,
  statusLabel,
}: Pick<EconomicDamageLine, "statusTone" | "statusLabel">) {
  if (statusTone === "muted") {
    return (
      <p className="text-muted-foreground mt-0.5 text-[11px]">{statusLabel}</p>
    );
  }

  const Icon = statusTone === "confirmed" ? CheckCircle2 : TriangleAlert;

  return (
    <p
      className={cn(
        "mt-0.5 flex items-center gap-1 text-[11px] font-medium",
        statusTone === "confirmed" ? "text-emerald-600" : "text-amber-600",
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span>{statusLabel}</span>
    </p>
  );
}

function DamageBreakdownRow({ line }: { line: EconomicDamageLine }) {
  return (
    <div className="py-2.5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-foreground text-sm font-medium">{line.label}</p>
        <p className="text-foreground shrink-0 text-sm font-semibold">
          {formatCurrency(line.amount)}
        </p>
      </div>
      <DamageLineStatus
        statusTone={line.statusTone}
        statusLabel={line.statusLabel}
      />
    </div>
  );
}

export function TotalDamagesTopStat({
  matter,
  isExpanded,
  onExpandedChange,
  className,
}: {
  matter: Matter;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  className?: string;
}) {
  const damages = getMatterEconomicDamagesSummary(matter);
  const hasDamages = damages.total > 0;
  const visibleLines = damages.lines.filter((line) => line.amount > 0);

  return (
    <div
      className={cn("min-w-0 border-r px-4 py-2.5 last:border-r-0", className)}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-[10px] font-semibold">
          Total damages
        </p>
        {hasDamages ? (
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground h-auto shrink-0 px-0 py-0 text-[10px] font-medium hover:bg-transparent"
            onClick={() => {
              onExpandedChange(!isExpanded);
            }}
            aria-expanded={isExpanded}
          >
            {isExpanded ? "Show less" : "Show more"}
            <ChevronDown
              className={cn(
                "ml-0.5 h-3 w-3 transition-transform",
                isExpanded ? "rotate-180" : "",
              )}
            />
          </Button>
        ) : null}
      </div>
      <p className="text-foreground mt-1 text-[15px] leading-tight font-semibold">
        {formatCurrency(damages.total)}
      </p>
      <p className="text-muted-foreground mt-1 text-xs">Economic damages</p>

      {isExpanded && hasDamages ? (
        <div className="mt-3 border-t pt-3">
          <p className="text-muted-foreground text-[10px] font-semibold">
            Damages breakdown
          </p>
          <div className="mt-1 divide-y">
            {visibleLines.map((line) => (
              <DamageBreakdownRow key={line.label} line={line} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
